import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, getAuthUser, saveAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, LogIn, KeyRound, ArrowRight } from "lucide-react";

/**
 * Post-purchase card: prompts the customer to create a portal account
 * (or log into an existing one) so they can view all their orders.
 */
export default function CustomerAccountPrompt({ email, name }) {
  const navigate = useNavigate();
  const currentUser = getAuthUser();
  const [status, setStatus] = useState("checking"); // checking | new | existing | logged
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentUser?.role === "customer" && currentUser?.email === email) {
      setStatus("logged");
      return;
    }
    api
      .post("/customers/check", { email })
      .then((r) => setStatus(r.data.exists ? "existing" : "new"))
      .catch(() => setStatus("new"));
  }, [email, currentUser]);

  const register = async (e) => {
    e.preventDefault();
    if (password.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/customers/register", { email, password, name });
      // If backend requires email verification, do NOT auto-login.
      if (data?.pending_verification) {
        setStatus("pending-verification");
        toast.success("Te enviamos un correo para confirmar tu cuenta");
        return;
      }
      if (data?.token && data?.user) {
        saveAuth(data.token, data.user);
        toast.success("Cuenta creada. Redirigiendo a tu portal...");
        setTimeout(() => navigate("/mi-cuenta"), 700);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  const resendConfirmation = async () => {
    setLoading(true);
    try {
      await api.post("/customers/resend-confirmation", { email });
      toast.success("Nuevo enlace enviado. Revisa tu correo.");
    } catch {
      toast.error("Error al reenviar el correo");
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.user.role !== "customer") {
        toast.error("Esta cuenta no es de cliente");
        setLoading(false);
        return;
      }
      saveAuth(data.token, data.user);
      toast.success("Sesión iniciada");
      setTimeout(() => navigate("/mi-cuenta"), 500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  if (status === "checking") return null;

  if (status === "pending-verification") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-xl p-6 md:p-8 max-w-xl mx-auto text-left"
        data-testid="account-pending-verification"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md bg-yellow-400/15 flex items-center justify-center border border-yellow-400/40">
            <UserPlus className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-yellow-300 font-mono-display">
              Confirma tu correo
            </div>
            <div className="font-display font-bold text-white text-lg">
              Revisa tu bandeja de entrada
            </div>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
          Enviamos un enlace de confirmación a <b className="text-yellow-300 break-all">{email}</b>.
          Haz clic en el botón del correo para activar tu cuenta y ver tu portal.
        </p>
        <p className="text-xs text-zinc-500 mb-4">
          Si no lo encuentras, revisa la carpeta de <b>spam</b> o solicita un reenvío.
        </p>
        <Button
          onClick={resendConfirmation}
          disabled={loading}
          data-testid="resend-verification-btn"
          className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
        >
          {loading ? "Enviando..." : "Reenviar correo de confirmación"}
        </Button>
      </motion.div>
    );
  }

  if (status === "logged") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-xl p-6 md:p-8 max-w-xl mx-auto text-left"
        data-testid="account-logged-panel"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md bg-emerald-500/20 flex items-center justify-center border border-emerald-500/40">
            <KeyRound className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-300 font-mono-display">
              Sesión activa
            </div>
            <div className="font-display font-bold text-white">
              Ya tienes tu cuenta lista
            </div>
          </div>
        </div>
        <Button
          onClick={() => navigate("/mi-cuenta")}
          data-testid="go-to-portal-btn"
          className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
        >
          Ver mi historial <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={status}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="glass rounded-xl p-6 md:p-8 max-w-xl mx-auto text-left"
        data-testid={status === "new" ? "account-register-panel" : "account-login-panel"}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-yellow-400/15 flex items-center justify-center border border-yellow-400/30">
            {status === "new" ? (
              <UserPlus className="w-5 h-5 text-yellow-400" />
            ) : (
              <LogIn className="w-5 h-5 text-yellow-400" />
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-yellow-300 font-mono-display">
              {status === "new" ? "Crea tu cuenta" : "Bienvenido de vuelta"}
            </div>
            <div className="font-display font-bold text-white text-lg">
              {status === "new"
                ? "Guarda tu historial de pedidos"
                : "Ya tienes una cuenta con este correo"}
            </div>
          </div>
        </div>

        <p className="text-sm text-zinc-400 mb-5">
          {status === "new"
            ? "Crea una contraseña para acceder a tu portal personal con todos tus pedidos y códigos."
            : "Ingresa tu contraseña para vincular este pedido a tu cuenta y ver tu historial."}
        </p>

        <form onSubmit={status === "new" ? register : login} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
              Email
            </Label>
            <Input
              value={email}
              disabled
              data-testid="account-email-input"
              className="bg-zinc-900/60 border-white/10 text-zinc-400 font-mono-display"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
              {status === "new" ? "Crea una contraseña" : "Tu contraseña"}
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoFocus
              required
              data-testid="account-password-input"
              className="bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="account-submit-btn"
            className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
          >
            {loading
              ? "Procesando..."
              : status === "new"
              ? "Crear cuenta y ver portal"
              : "Ingresar a mi portal"}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </form>
      </motion.div>
    </AnimatePresence>
  );
}
